import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { useDialogs } from '../../components/PromptDialog';
import { useCanWrite } from '../auth/permissions';
import { fetchCalendar, patchPost, socialKeys } from './api';
import { ChannelBar, EmptyHint, KuerzelChip, dauer, kanalKurz, kanalName, reichweiteText, tagKurz } from './bausteine';
import { FreigabeBadge, LAGE_FILTER, PlattformChip, brauchtDich, istLageFilter, terminHinweis } from './lage';
import { PUBLISH_CHANNELS, type Channel, type FreierSlot, type Termin } from './types';

const t = darkTheme.colors;

/** Der Monat, in dem `heute` liegt — als `YYYY-MM`. */
function aktuellerMonat(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monatsGrenzen(monat: string): { from: string; to: string } {
  const [j, m] = monat.split('-').map(Number);
  const jahr = j ?? new Date().getFullYear();
  const mon = m ?? 1;
  const letzter = new Date(Date.UTC(jahr, mon, 0)).getUTCDate();
  return { from: `${monat}-01`, to: `${monat}-${String(letzter).padStart(2, '0')}` };
}

function monatVerschieben(monat: string, delta: number): string {
  const [j, m] = monat.split('-').map(Number);
  const d = new Date(Date.UTC(j ?? 2026, (m ?? 1) - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** So lange wartet die Suche nach dem letzten Tastendruck, bevor sie den Server fragt. */
const SUCHE_VERZOEGERUNG_MS = 300;

const heuteStr = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

type Zeile =
  | { art: 'termin'; date: string; termin: Termin }
  | { art: 'slot'; date: string; slot: FreierSlot };

/** Beispiel-Link je Plattform für den Eintrage-Dialog. */
const LINK_BEISPIEL: Record<string, string> = {
  YOUTUBE: 'https://youtube.com/shorts/…',
  INSTAGRAM: 'https://www.instagram.com/reel/…',
  TIKTOK: 'https://www.tiktok.com/@currivium/video/…',
  FACEBOOK: 'https://www.facebook.com/…',
};

/**
 * Kalender — Agenda und Monatsraster (CUR-323, CUR-332).
 *
 * **Agenda ist der Standard.** Man arbeitet „was kommt als Nächstes", nicht
 * „wie sieht der Monat aus". Das Raster beantwortet „ist die Woche zu voll?",
 * die Agenda beantwortet „was mache ich jetzt" — und die zweite Frage stellt
 * sich täglich, die erste einmal im Monat.
 *
 * Die Ansicht steht in der URL (`?view=monat`), damit ein Link sie mitbringt.
 *
 * **Die Suche (`?q=`, CUR-2287) fragt den Server, nicht die geladene Liste.**
 * Die Agenda lädt einen Monat; eine Suche darin fände E31 nicht, sobald sein
 * Termin im Vormonat lag. Mit `q` sucht der Server über alle Monate, und die
 * Seite zeigt die Treffer als Agenda — ein Monatsraster kann Treffer aus drei
 * Monaten nicht zeigen.
 */
export function KalenderPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const [monat, setMonat] = useState(aktuellerMonat);
  const ansicht = params.get('view') === 'monat' ? 'monat' : 'agenda';
  const kanal = (params.get('channel') as Channel | null) ?? undefined;
  const slotsZeigen = params.get('slots') !== 'aus';
  const lageParam = params.get('lage');
  const lage = istLageFilter(lageParam) ? lageParam : null;
  const lageFilter = LAGE_FILTER.find((f) => f.key === lage) ?? null;
  const suchParam = (params.get('q') ?? '').trim();
  // Das Feld hält, was getippt wird; die URL erst, was gesucht wird — sonst
  // fragte jeder Tastendruck den Server.
  const [suchText, setSuchText] = useState(suchParam);
  const sucht = suchParam !== '';
  // Was die Seite zuletzt selbst in die URL geschrieben hat. Ändert sich `q` von
  // außen (Zurück im Browser, ein Link), folgt das Feld — sonst schriebe der
  // Effekt unten den alten Text sofort wieder hinein. Eigene Schreibvorgänge
  // erkennt der Vergleich und lässt den Text stehen, der gerade getippt wird.
  const zuletztGeschrieben = useRef(suchParam);
  useEffect(() => {
    if (suchParam === zuletztGeschrieben.current) return;
    zuletztGeschrieben.current = suchParam;
    setSuchText(suchParam);
  }, [suchParam]);
  const canWrite = useCanWrite();
  const dialogs = useDialogs();
  const qc = useQueryClient();
  const [fehler, setFehler] = useState<string | null>(null);
  // Mobil stehen Kanal- und Lage-Filter hinter einem Knopf (Spec §5.3) — sonst
  // kämen Kopf, Rhythmen, zwei Filterreihen und „Ohne Termin" vor der ersten Zeile.
  const [filterOffen, setFilterOffen] = useState(false);
  const aktiveFilter = (kanal ? 1 : 0) + (lage ? 1 : 0) + (sucht ? 1 : 0);

  useEffect(() => {
    const gesucht = suchText.trim();
    if (gesucht === suchParam) return undefined;
    const timer = window.setTimeout(() => {
      zuletztGeschrieben.current = gesucht;
      // Funktional, weil sich die übrigen Parameter (Kanal, Lage) in der
      // Wartezeit geändert haben können.
      setParams((vorher) => {
        const next = new URLSearchParams(vorher);
        if (gesucht) next.set('q', gesucht);
        else next.delete('q');
        return next;
      }, { replace: true });
    }, SUCHE_VERZOEGERUNG_MS);
    return () => window.clearTimeout(timer);
  }, [suchText, suchParam, setParams]);

  // „Von Hand erledigt — Link eintragen": derselbe PATCH wie im Cockpit. Die
  // Sperre ohne 👍 und die Plattform-Prüfung des Links stehen im Server.
  const eintragen = useMutation({
    mutationFn: ({ id, url }: { id: string; url: string }) => patchPost(id, { status: 'GEPOSTET', postedUrl: url }),
    onSuccess: () => {
      setFehler(null);
      void qc.invalidateQueries({ queryKey: ['social'] });
    },
    onError: (e: Error) => setFehler(e.message),
  });
  const linkEintragen = async (termin: Termin): Promise<void> => {
    const url = await dialogs.prompt({
      title: `${kanalName(termin.channel)}: von Hand erledigt`,
      label: 'Link zum Beitrag',
      placeholder: LINK_BEISPIEL[termin.channel] ?? 'https://…',
      message: `${termin.asset.title} — der Link macht den Kalender zum Beleg.`,
      confirmLabel: 'Als live eintragen',
    });
    if (url) eintragen.mutate({ id: termin.id, url: url.trim() });
  };

  const { from, to } = monatsGrenzen(monat);
  const kalender = useQuery({
    queryKey: socialKeys.calendar(from, to, kanal, slotsZeigen, suchParam || undefined),
    queryFn: () => fetchCalendar({ from, to, channel: kanal, slots: slotsZeigen, q: suchParam || undefined }),
  });
  const trefferZahl = kalender.data
    ? kalender.data.termine.length + kalender.data.ohneTermin.length + kalender.data.fachreview.length
    : 0;

  const setParam = (k: string, v: string | null): void => {
    const next = new URLSearchParams(params);
    if (v === null) next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };

  const zeilen = useMemo<Zeile[]>(() => {
    const d = kalender.data;
    if (!d) return [];
    const termine = d.termine.filter((x) => (lageFilter ? lageFilter.passt(x) : true));
    const alle: Zeile[] = [
      ...termine.flatMap((x) => (x.date ? [{ art: 'termin' as const, date: x.date, termin: x }] : [])),
      // Freie Slots gehören zur Planung, nicht zu einer Lage-Frage.
      ...(slotsZeigen && !lageFilter ? d.freieSlots.map((s) => ({ art: 'slot' as const, date: s.date, slot: s })) : []),
    ];
    return alle.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  }, [kalender.data, slotsZeigen, lageFilter]);

  // Der Lage-Filter gilt auch für „Ohne Termin" — sonst meldete „Wartet auf
  // Christine" leer, während ein eingereichter Entwurf ohne Tag dort wartet.
  const ohneTermin = useMemo(() => {
    const alle = kalender.data?.ohneTermin ?? [];
    return lageFilter ? alle.filter(lageFilter.passt) : alle;
  }, [kalender.data, lageFilter]);

  const zaehler = useMemo(() => {
    const alle = [...(kalender.data?.termine ?? []), ...(kalender.data?.ohneTermin ?? [])];
    return Object.fromEntries(LAGE_FILTER.map((f) => [f.key, alle.filter(f.passt).length])) as Record<string, number>;
  }, [kalender.data]);

  /** Klappt einen Sammelblock auf und scrollt hin — auch wenn er von Hand zugeklappt wurde. */
  const blockZeigen = (id: string): void => {
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el instanceof HTMLDetailsElement) el.open = true;
      el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  };

  const heute = heuteStr();

  return (
    <div className="mx-auto max-w-[1200px] p-4 lg:p-6">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold" style={{ color: t.textPrimary }}>Kalender</h1>
          <Link to="/social/week" className="inline-block min-h-[44px] py-2 text-sm font-semibold" style={{ color: t.accentLight }}>Woche planen →</Link>
          <p className="text-sm" style={{ color: t.textTertiary }} aria-live="polite">
            {sucht
              ? kalender.data
                ? `Suche „${suchParam}" · ${trefferZahl} Treffer über alle Monate${kalender.data.suche?.gekuerzt ? ' · nur die neuesten — Suche genauer fassen' : ''}`
                : `Suche „${suchParam}" …`
              : (
                <>
                  {tagKurz(from)}–{tagKurz(to)} ·{' '}
                  {kalender.data
                    ? `${kalender.data.termine.length} Termine · ${zaehler['christine'] ?? 0} bei Christine · `
                      + `${zaehler['offen'] ?? 0} freigegeben, offen · ${zaehler['live'] ?? 0} live · `
                      + `${kalender.data.freieSlots.length} freie Slots`
                    : '…'}
                </>
              )}
          </p>
        </div>
        {/* Die Suche gilt über alle Monate — Monatswechsel und Raster hätten
            nichts zu zeigen, was die Trefferliste nicht schon zeigt. */}
        {!sucht && <div className="flex items-center gap-2">
          <button type="button" onClick={() => setMonat(monatVerschieben(monat, -1))}
            className="min-h-[44px] rounded-md px-3" style={{ backgroundColor: t.bgElevated, color: t.textPrimary }}>
            ‹
          </button>
          <button type="button" onClick={() => setMonat(aktuellerMonat())}
            className="min-h-[44px] rounded-md px-3 text-sm" style={{ backgroundColor: t.bgElevated, color: t.textPrimary }}>
            Heute
          </button>
          <button type="button" onClick={() => setMonat(monatVerschieben(monat, 1))}
            className="min-h-[44px] rounded-md px-3" style={{ backgroundColor: t.bgElevated, color: t.textPrimary }}>
            ›
          </button>
          <div className="ml-2 flex rounded-md" style={{ backgroundColor: t.bgElevated }}>
            {(['agenda', 'monat'] as const).map((a) => (
              <button key={a} type="button" onClick={() => setParam('view', a === 'agenda' ? null : a)}
                className="min-h-[44px] rounded-md px-3 text-sm font-semibold"
                style={{
                  backgroundColor: ansicht === a ? t.accent : 'transparent',
                  color: ansicht === a ? '#fff' : t.textSecondary,
                }}>
                {a === 'agenda' ? 'Agenda' : 'Monat'}
              </button>
            ))}
          </div>
        </div>}
      </header>

      {kalender.data && kalender.data.rhythmen.length > 0 && (
        <RhythmenLeiste rhythmen={kalender.data.rhythmen} />
      )}

      {kalender.data && (
        <ChristineLeiste lagen={kalender.data.lagen}
          onSocial={() => setParam('lage', 'christine')}
          onApp={() => blockZeigen('block-fachreview')} />
      )}

      <button type="button" onClick={() => setFilterOffen((v) => !v)}
        aria-expanded={filterOffen} aria-controls="kalender-filter"
        className="mb-2 min-h-[44px] rounded-md px-3 text-sm font-semibold lg:hidden"
        style={{
          backgroundColor: aktiveFilter > 0 ? t.accentSubtle : t.bgElevated,
          color: aktiveFilter > 0 ? t.accentLight : t.textSecondary,
        }}>
        Filter{aktiveFilter > 0 ? ` (${aktiveFilter})` : ''}
      </button>

      <div id="kalender-filter" className={filterOffen ? 'block' : 'hidden lg:block'}>
        <Suchfeld wert={suchText} onChange={setSuchText} />
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <FilterKnopf aktiv={!kanal} onClick={() => setParam('channel', null)}>Alle Kanäle</FilterKnopf>
          {PUBLISH_CHANNELS.map((c) => (
            <FilterKnopf key={c} aktiv={kanal === c} onClick={() => setParam('channel', c)}>
              {kanalName(c)}
            </FilterKnopf>
          ))}
          <label className="ml-2 flex min-h-[44px] cursor-pointer items-center gap-2 text-sm" style={{ color: t.textSecondary }}>
            <input type="checkbox" checked={slotsZeigen} onChange={(e) => setParam('slots', e.target.checked ? null : 'aus')} />
            freie Slots zeigen
          </label>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2" role="group" aria-label="Nach Lage filtern">
          <FilterKnopf aktiv={!lage} onClick={() => setParam('lage', null)}>Alle</FilterKnopf>
          {LAGE_FILTER.map((f) => (
            <FilterKnopf key={f.key} aktiv={lage === f.key} onClick={() => setParam('lage', f.key)}>
              {f.label}{f.key !== 'live' ? ` (${zaehler[f.key] ?? 0})` : ''}
            </FilterKnopf>
          ))}
        </div>
      </div>

      {fehler && (
        <p role="alert" className="mb-4 rounded-md border p-3 text-sm" style={{ borderColor: t.warning, color: t.warning }}>
          {fehler}
        </p>
      )}

      {ohneTermin.length > 0 && (
        <Sammelblock id="block-ohne-termin" titel="Ohne Termin" termine={ohneTermin}
          offen={lageFilter !== null || sucht}
          onLinkEintragen={canWrite ? (x) => void linkEintragen(x) : undefined} />
      )}
      {kalender.data && kalender.data.fachreview.length > 0 && !lageFilter && (
        <Sammelblock id="block-fachreview" titel="Fachreview · App-Fassungen" termine={kalender.data.fachreview}
          offen={sucht} />
      )}

      {kalender.isLoading && <Skelett />}
      {kalender.isError && (
        <p className="rounded-md border p-4 text-sm" style={{ borderColor: t.danger, color: t.danger }}>
          Kalender konnte nicht geladen werden.
        </p>
      )}

      {kalender.data && sucht && trefferZahl === 0 && (
        <p className="rounded-md border p-4 text-sm" style={{ borderColor: t.borderSubtle, color: t.textSecondary }}>
          {kalender.data.suche?.art === 'KUERZEL'
            ? `Kein Eintrag mit dem Kürzel ${suchParam.toUpperCase()}. Ein Kürzel findet nur Einträge, deren Ordner genau so heißt — Entwürfe ohne Ordner nicht.`
            : `Kein Titel enthält „${suchParam}".`}
        </p>
      )}
      {kalender.data && sucht && trefferZahl > 0 && lageFilter && zeilen.length === 0 && ohneTermin.length === 0 && (
        <p className="rounded-md border p-4 text-sm" style={{ borderColor: t.borderSubtle, color: t.textSecondary }}>
          Treffer gibt es, aber keiner passt zum Filter „{lageFilter.label}".
        </p>
      )}

      {kalender.data && zeilen.length === 0 && !lageFilter && !sucht && (
        <EmptyHint
          title="Für diesen Monat steht nichts an. Falls der Bestand noch leer ist, füllt ihn der Sync-Lauf:"
          command="ADMIN_TOKEN=… node scripts/social/sync-assets.mjs"
        />
      )}

      {kalender.data && zeilen.length === 0 && ohneTermin.length === 0 && lageFilter && !sucht && (
        <p className="rounded-md border p-4 text-sm" style={{ borderColor: t.borderSubtle, color: t.textSecondary }}>
          {/* Die Leiste zählt über alle Monate, der Filter nur diesen — der Text
              darf ihr nicht widersprechen. */}
          {lageFilter.key === 'christine'
            ? kalender.data.lagen.wartetSocial > 0
              ? 'In diesem Monat wartet nichts auf Christine — die übrigen Einreichungen liegen in einem anderen Monat.'
              : 'Nichts wartet auf Christine.'
            : lageFilter.key === 'offen' ? 'Alles Freigegebene ist live.'
            : 'In diesem Monat passt nichts zu diesem Filter.'}
        </p>
      )}

      {kalender.data && zeilen.length > 0 && (ansicht === 'agenda' || sucht) && (
        <Agenda zeilen={zeilen} heute={heute} onLinkEintragen={canWrite ? (x) => void linkEintragen(x) : undefined} />
      )}
      {kalender.data && zeilen.length > 0 && ansicht === 'monat' && !sucht && (
        <MonatsRaster monat={monat} zeilen={zeilen} heute={heute} />
      )}
      {dialogs.dialog}
    </div>
  );
}

/**
 * „Wartet auf Christine: 2 Social · 10 App-Fassungen" — über alle Monate.
 *
 * Die App-Fassungen stehen nicht im Kalender (sie werden nie veröffentlicht),
 * aber die Frage „ist es bei ihr eingereicht?" galt am 13.10. vor allem ihnen.
 * Aus der Agenda nehmen ja, aus dem Blick nehmen nein (Spec §3.3).
 */
function ChristineLeiste({ lagen, onSocial, onApp }: {
  lagen: { wartetSocial: number; wartetApp: number };
  onSocial: () => void;
  onApp: () => void;
}): JSX.Element | null {
  if (lagen.wartetSocial + lagen.wartetApp === 0) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm"
      style={{ borderColor: t.borderSubtle, backgroundColor: t.infoBg, color: t.info }}>
      <span className="font-semibold">Wartet auf Christine:</span>
      <button type="button" onClick={onSocial} className="min-h-[32px] underline-offset-2 hover:underline">
        {lagen.wartetSocial} Social
      </button>
      <span aria-hidden>·</span>
      <button type="button" onClick={onApp} className="min-h-[32px] underline-offset-2 hover:underline">
        {lagen.wartetApp} App-Fassungen ›
      </button>
    </div>
  );
}

/**
 * Zusammenklappbarer Block für Posts ohne Kalendertag.
 *
 * Ungesteuert: `open` als Prop setzte React nur bei einer Änderung — wer den
 * Block von Hand zuklappt, bekäme ihn über dieselbe Aktion nie wieder auf.
 */
function Sammelblock({ id, titel, termine, offen, onLinkEintragen }: {
  id: string;
  titel: string;
  termine: Termin[];
  offen: boolean;
  onLinkEintragen?: (t: Termin) => void;
}): JSX.Element {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (offen && ref.current) ref.current.open = true;
  }, [offen]);
  return (
    <details id={id} ref={ref} className="mb-4 scroll-mt-4 rounded-md border p-2" style={{ borderColor: t.borderSubtle }}>
      <summary className="min-h-[44px] cursor-pointer px-1 py-2 text-xs font-semibold uppercase tracking-wide"
        style={{ color: t.textTertiary }}>
        {titel} ({termine.length})
      </summary>
      <ul className="mt-1 space-y-1">
        {termine.map((x) => <TerminZeile key={x.id} termin={x} onLinkEintragen={onLinkEintragen} />)}
      </ul>
    </details>
  );
}

/**
 * Suche über alle Monate (CUR-2287). Über den Filtern, mobil mit ihnen hinter
 * dem Knopf „Filter (n)" (Spec §16.4).
 */
function Suchfeld({ wert, onChange }: { wert: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <label htmlFor="kalender-suche" className="sr-only">Kalender durchsuchen</label>
      <input
        id="kalender-suche"
        type="search"
        value={wert}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Kürzel (E31) oder Titel"
        autoComplete="off"
        spellCheck={false}
        className="min-h-[44px] w-full max-w-[360px] rounded-md border px-3 text-sm"
        style={{ backgroundColor: t.bgElevated, borderColor: t.borderSubtle, color: t.textPrimary }}
      />
      {wert !== '' && (
        <button type="button" onClick={() => onChange('')}
          className="min-h-[44px] rounded-md px-3 text-sm" style={{ backgroundColor: t.bgElevated, color: t.textSecondary }}>
          Suche aufheben
        </button>
      )}
    </div>
  );
}

function FilterKnopf({ aktiv, onClick, children }: { aktiv: boolean; onClick: () => void; children: React.ReactNode }): JSX.Element {
  return (
    <button type="button" onClick={onClick}
      className="min-h-[44px] rounded-md px-3 text-sm font-semibold"
      style={{
        backgroundColor: aktiv ? t.accentSubtle : t.bgElevated,
        color: aktiv ? t.accentLight : t.textSecondary,
      }}>
      {children}
    </button>
  );
}

/**
 * Der Vorrats-Anzeiger — die Zahl, die beim Planen zuerst zählt.
 *
 * Ein leerer wiederkehrender Slot sagt nach außen lauter „hier hat jemand
 * aufgehört" als eine unregelmäßige Veröffentlichung, die nichts verspricht.
 * Deshalb steht hier die Reichweite und nicht der Rhythmus.
 *
 * **Warnung in `warning`, nie in `danger`.** Es ist ein Redaktionsplan, kein
 * Einsatzleitsystem — und `danger` ist im Haus die Farbe für „etwas ist
 * kaputt", nicht für „das wird bald knapp".
 */
function RhythmenLeiste({ rhythmen }: { rhythmen: import('./types').Rhythmus[] }): JSX.Element {
  const WD = ['', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  return (
    <div className="mb-4 rounded-md border p-3" style={{ borderColor: t.borderSubtle, backgroundColor: t.bgSurface }}>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: t.textTertiary }}>
        Rhythmen · Reichweite des Vorrats
      </p>
      <ul className="space-y-1">
        {rhythmen.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2">
              <span style={{ color: t.textSecondary }}>
                {r.weekdays.map((w) => WD[w]).join(' · ')}
              </span>
              <span className="font-semibold" style={{ color: t.textPrimary }}>{r.stream}</span>
              <ChannelBar channel={r.channel} />
            </span>
            <span className="flex items-center gap-3">
              <span style={{ color: t.textTertiary }}>{r.vorrat} Folgen</span>
              <span style={{ color: r.knapp ? t.warning : t.textSecondary }}>
                {r.knapp ? '⚠ ' : ''}{reichweiteText(r.reichweite)}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Agenda({ zeilen, heute, onLinkEintragen }: {
  zeilen: Zeile[];
  heute: string;
  onLinkEintragen?: (t: Termin) => void;
}): JSX.Element {
  const tage = [...new Set(zeilen.map((z) => z.date))];
  return (
    <div className="space-y-4">
      {tage.map((tag) => (
        <section key={tag}>
          <h2
            className="sticky top-0 z-10 py-1 text-xs font-semibold uppercase tracking-wide"
            style={{
              backgroundColor: t.bgBase,
              color: tag === heute ? t.accentLight : t.textTertiary,
            }}
          >
            {tagKurz(tag)}
            {tag === heute ? ' · heute' : ''}
            {tag < heute ? ' · vergangen' : ''}
          </h2>
          <ul className="space-y-1">
            {zeilen.filter((z) => z.date === tag).map((z) =>
              z.art === 'termin'
                ? <TerminZeile key={z.termin.id} termin={z.termin} onLinkEintragen={onLinkEintragen} />
                : <SlotZeile key={`${z.slot.ruleId}-${z.slot.date}`} slot={z.slot} />,
            )}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * Eine Zeile beantwortet zwei Fragen ohne Klick (Spec §5.1): „Wo steht die
 * Freigabe?" (Badge) und „Wo ist es live?" (Plattform-Chip mit Link).
 *
 * Die StatusPill ist bewusst raus: „Geplant" hieß „hat einen Tag", und neben
 * einem Plattform-Chip las es sich wie „geht von selbst raus".
 *
 * Der Zeilen-Link umfasst nur Bild und Titel — der Chip trägt einen eigenen
 * Link und darf nicht darin stecken.
 */
function TerminZeile({ termin, onLinkEintragen }: { termin: Termin; onLinkEintragen?: (t: Termin) => void }): JSX.Element {
  const hinweis = terminHinweis(termin);
  return (
    <li className="flex min-h-[44px] flex-wrap items-center gap-x-3 gap-y-2 rounded-md border-l-2 px-3 py-2"
      style={{
        // Der Rand heißt „braucht dich" — in `warning`, nie in `danger`.
        borderLeftColor: brauchtDich(termin) ? t.warning : t.borderSubtle,
        backgroundColor: t.bgSurface,
      }}>
      {/* Unter `lg` nimmt der Titel die ganze Zeile, Badge und Chip brechen darunter
          um — nebeneinander überlagerte das Badge sonst den umbrechenden Titel. */}
      <Link to={`/social/post/${termin.id}`} className="flex w-full min-w-0 items-center gap-3 hover:opacity-90 lg:w-auto lg:flex-1">
        {termin.asset.thumbnailUrl ? (
          <img src={termin.asset.thumbnailUrl} alt="" className="h-12 w-12 shrink-0 rounded object-cover" />
        ) : (
          <span aria-hidden className="h-12 w-12 shrink-0 rounded" style={{ backgroundColor: t.bgElevated }} />
        )}
        <span className="flex min-w-0 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            <ChannelBar channel={termin.channel} />
            <KuerzelChip kuerzel={termin.asset.kuerzel} ordner={termin.asset.ordner} />
            <span className="text-sm font-medium" style={{ color: t.textPrimary }}>{termin.asset.title}</span>
          </span>
          <span className="text-xs" style={{ color: t.textTertiary }}>
            {termin.asset.aspectRatio ?? ''} {dauer(termin.asset.durationSec)}
            {!termin.date && ' · ohne Termin'}
          </span>
        </span>
      </Link>
      <FreigabeBadge review={termin.review} />
      {/* Eine App-Fassung wird nie veröffentlicht — ein Plattform-Chip „App wartet"
          behauptete einen Weg, den es nicht gibt. */}
      {termin.channel !== 'APP' && (
        <PlattformChip channel={termin.channel} publication={termin.publication}
          onLinkEintragen={onLinkEintragen ? () => onLinkEintragen(termin) : undefined} />
      )}
      {hinweis && (
        <span className="w-full text-xs font-semibold lg:w-auto" style={{ color: t.warning }}>{hinweis}</span>
      )}
    </li>
  );
}

/**
 * Ein freier Slot ist gestrichelt — er ist ein Vorschlag, kein Termin.
 * Sähe er aus wie ein Termin, zählte man ihn beim Überfliegen mit.
 */
function SlotZeile({ slot }: { slot: FreierSlot }): JSX.Element {
  return (
    <li>
      <Link
        to={`/social/assets?stream=${encodeURIComponent(slot.stream)}&slot=${slot.date}&channel=${slot.channel}`}
        className="flex min-h-[44px] flex-wrap items-center gap-3 rounded-md border border-dashed px-3 py-2"
        style={{ borderColor: t.border, color: t.textTertiary }}
      >
        <ChannelBar channel={slot.channel} />
        <span className="flex-1 text-sm italic">frei · {slot.stream}</span>
        <span className="text-xs font-semibold" style={{ color: t.accentLight }}>Folge zuweisen ›</span>
      </Link>
    </li>
  );
}

/**
 * Monatsraster. Fällt unter `lg` auf die Agenda zurück, statt in einen
 * horizontalen Scroll zu verschwinden — ein Monatsraster, das man seitlich
 * schieben muss, hat keinen Wert.
 */
function MonatsRaster({ monat, zeilen, heute }: { monat: string; zeilen: Zeile[]; heute: string }): JSX.Element {
  const { from, to } = monatsGrenzen(monat);
  const erster = new Date(`${from}T00:00:00.000Z`);
  const vorlauf = (erster.getUTCDay() + 6) % 7; // Woche beginnt Montag
  const tage = Number(to.slice(-2));

  return (
    <>
      <div className="lg:hidden"><Agenda zeilen={zeilen} heute={heute} /></div>
      <div className="hidden lg:grid lg:grid-cols-7 lg:gap-1">
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((w) => (
          <div key={w} className="px-2 py-1 text-xs font-semibold uppercase" style={{ color: t.textTertiary }}>{w}</div>
        ))}
        {Array.from({ length: vorlauf }, (_, i) => <div key={`leer-${i}`} />)}
        {Array.from({ length: tage }, (_, i) => {
          const tag = `${monat}-${String(i + 1).padStart(2, '0')}`;
          const drin = zeilen.filter((z) => z.date === tag);
          return (
            <div key={tag} className="min-h-[92px] rounded-md p-1"
              style={{
                backgroundColor: t.bgSurface,
                outline: tag === heute ? `1px solid ${t.accent}` : 'none',
              }}>
              <div className="mb-1 text-xs" style={{ color: tag === heute ? t.accentLight : t.textTertiary }}>
                {i + 1}
              </div>
              {drin.map((z) =>
                z.art === 'termin' ? (
                  // Das Kürzel statt der Kennung: Die hieß bei Entwürfen `review-9f3c…` (CUR-2287).
                  // Ohne Kürzel der Titel — gekürzt, aber lesbar.
                  <Link key={z.termin.id} to={`/social/post/${z.termin.id}`}
                    title={z.termin.asset.title}
                    className="mb-1 block truncate rounded px-1 py-0.5 text-xs"
                    style={{
                      backgroundColor: t.bgElevated,
                      color: brauchtDich(z.termin) ? t.warning : t.textSecondary,
                    }}>
                    {z.termin.publication.state === 'LIVE' ? '✓ ' : ''}
                    {z.termin.asset.kuerzel
                      ? <><span className="font-mono font-semibold">{z.termin.asset.kuerzel}</span> {kanalKurz(z.termin.channel)}</>
                      : z.termin.asset.title}
                  </Link>
                ) : (
                  <div key={`${z.slot.ruleId}-${z.slot.date}`}
                    className="mb-1 truncate rounded border border-dashed px-1 py-0.5 text-xs"
                    style={{ borderColor: t.border, color: t.textTertiary }}>
                    frei
                  </div>
                ),
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

/** Skelett nach Hausmuster — der Aufbau ist bekannt und kann vorgezeichnet werden. */
function Skelett(): JSX.Element {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="h-12 animate-pulse rounded-md" style={{ backgroundColor: t.bgSurface }} />
      ))}
    </div>
  );
}
